import type { GiveBackView, SlipSnapshot } from '@didto/domain';
import type { Row } from './db';
import { useDeviceStore } from './deviceStore';
import { supabase } from './supabase';

/** Payment reads (RLS-guarded selects). Writes go through the outbox (runQueuedCommand). */
export type PaymentMethod = Pick<
  Row<'payment_method'>,
  | 'id'
  | 'name'
  | 'kind'
  | 'enabled'
  | 'requires_reference'
  | 'opens_cash_drawer'
  | 'allowed_offline'
  | 'sort'
  | 'archived_at'
  | 'reference_pattern'
>;
export type Payment = Pick<
  Row<'payment'>,
  | 'id'
  | 'order_id'
  | 'payment_method_id'
  | 'method_name_snapshot'
  | 'method_kind_snapshot'
  | 'amount_minor'
  | 'tendered_minor'
  | 'change_minor'
  | 'reference'
  | 'note'
  | 'status'
  | 'received_by_employee_id'
  | 'captured_at'
  | 'recorded_at'
  | 'owner_entry'
>;
export type Reversal = Pick<
  Row<'reversal'>,
  | 'id'
  | 'order_id'
  | 'payment_id'
  | 'kind'
  | 'amount_minor'
  | 'payment_method_id'
  | 'method_name_snapshot'
  | 'reason'
  | 'method_override'
  | 'performed_by_employee_id'
  | 'approved_by_employee_id'
  | 'occurred_at'
  | 'recorded_at'
  | 'owner_entry'
> & {
  /** A refund the owner recorded on a closed shift and then cancelled (ADR-028): in the history, in no sum. */
  cancelled: boolean;
};
export type Receipt = Pick<
  Row<'receipt'>,
  'id' | 'order_id' | 'reversal_id' | 'display_number' | 'kind' | 'official' | 'issued_at'
> & { snapshot: SlipSnapshot };

export const paymentKeys = {
  methods: ['payment-methods'] as const,
  money: (orderId: string) => ['orders', 'money', orderId] as const,
};

async function rows<T>(q: PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export const fetchPaymentMethods = () =>
  rows<PaymentMethod>(
    supabase
      .from('payment_method')
      .select(
        'id, name, kind, enabled, requires_reference, opens_cash_drawer, allowed_offline, sort, archived_at, reference_pattern',
      )
      .order('sort'),
  );

export interface OrderMoney {
  payments: Payment[];
  reversals: Reversal[];
  receipts: Receipt[];
}

/** Tenders, reversals and slips of one order. `voidedPaymentIds` derives from the void reversals. */
export async function fetchOrderMoney(orderId: string): Promise<OrderMoney> {
  const payments = await rows<Payment>(
    supabase
      .from('payment')
      .select(
        'id, order_id, payment_method_id, method_name_snapshot, method_kind_snapshot, amount_minor, tendered_minor, change_minor, reference, note, status, received_by_employee_id, captured_at, recorded_at, owner_entry',
      )
      .eq('order_id', orderId)
      .order('recorded_at'),
  );
  const rawReversals = await rows<
    Omit<Reversal, 'cancelled'> & { reversal_cancel: { id: string }[] }
  >(
    supabase
      .from('reversal')
      .select(
        'id, order_id, payment_id, kind, amount_minor, payment_method_id, method_name_snapshot, reason, method_override, performed_by_employee_id, approved_by_employee_id, occurred_at, recorded_at, owner_entry, reversal_cancel(id)',
      )
      .eq('order_id', orderId)
      .order('recorded_at'),
  );
  const reversals: Reversal[] = rawReversals.map(({ reversal_cancel, ...r }) => ({
    ...r,
    cancelled: reversal_cancel.length > 0,
  }));
  const rawReceipts = await rows<Omit<Receipt, 'snapshot'> & { snapshot: unknown }>(
    supabase
      .from('receipt')
      .select('id, order_id, reversal_id, display_number, kind, official, issued_at, snapshot')
      .eq('order_id', orderId)
      .order('issued_at'),
  );
  // The server builds the snapshot (app.slip_snapshot); the JSON column carries that shape.
  const receipts = rawReceipts.map((r) => ({ ...r, snapshot: r.snapshot as SlipSnapshot }));
  return { payments, reversals, receipts };
}

export function voidedPaymentIds(reversals: readonly Reversal[]): Set<string> {
  return new Set(
    reversals.filter((r) => r.kind === 'void' && r.payment_id).map((r) => r.payment_id as string),
  );
}

export const METHOD_KIND_LABEL: Record<PaymentMethod['kind'], string> = {
  cash: 'Cash',
  ewallet: 'E-wallet',
  card: 'Card',
  bank_transfer: 'Bank transfer',
  complimentary: 'Complimentary',
  other: 'Other',
  online: 'Online',
};

/* ── money given back by a non-cash method (ADR-029) ── */
export const giveBackKeys = {
  // under ['orders'], so every command's invalidation refreshes it
  order: (orderId: string) => ['orders', 'give-backs', orderId] as const,
};

/** The order's non-cash money given back, one entry per transfer, with what this person may still
 * give (the reference, a void's answer). Never the owner's marks. */
export async function fetchGiveBacks(orderId: string): Promise<GiveBackView[]> {
  const token = useDeviceStore.getState().sessionToken;
  const { data, error } = await supabase.rpc('order_give_backs', {
    p_order_id: orderId,
    p_session_token: token ?? undefined,
  });
  if (error) throw error;
  return (data as unknown as { give_backs: GiveBackView[] | null }).give_backs ?? [];
}
